import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PlayerForm from '../../../components/PlayerForm';
import { addPlayer } from '../../../api/club/playerService';
import { fetchTeamsByClubId } from '../../../api/club/teamService';
import { getUserClubIds } from '../../../utils/roleBasedAccess';
import './PlayerInformation.css';

const AddPlayer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const searchParams = new URLSearchParams(location.search);
  const teamId = searchParams.get('teamId') || '689308e06c6a15845ec2513e';

  const [teams, setTeams] = useState<Array<{ value: string; label: string; ageCategory: string }>>([]);
  const [fullTeamsData, setFullTeamsData] = useState<any[]>([]); // Store full team data for age categories
  const [selectedTeamId, setSelectedTeamId] = useState<string>(teamId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  useEffect(() => {
    loadTeams();
  }, []);



  const loadTeams = async () => {
    try {
      setTeamsError(null);
      
      // Get user's club IDs
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length === 0) {
        setTeamsError('No club access found for this user. Please contact your administrator.');
        return;
      }
      
      // Use the first club ID (assuming single club access for now)
      const clubId = clubIds[0];
      
      // Fetch teams from API with dynamic club ID
      const teamsData = await fetchTeamsByClubId(clubId);
      
      // Store full team data for age category access
      setFullTeamsData(teamsData);
      
      const transformedTeams = teamsData.map((team: any) => ({ 
        value: team._id, 
        label: team.ageCategory 
          ? `${team.name} - ${team.ageCategory}` 
          : team.name,
        ageCategory: team.ageCategory || ''
      }));
      setTeams(transformedTeams);
    } catch (error: any) {
      console.error('Failed to load teams:', error);
      setTeamsError(error.message || 'Failed to load teams. Please try again.');
    }
  };

  const handleSavePlayer = async (playerData: any) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Get age category from selected team
      const selectedTeam = fullTeamsData.find(team => team._id === selectedTeamId);
      const ageCategory = selectedTeam?.ageCategory || playerData.ageCategory;
      
      const addData = {
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: ageCategory,
        parentName: playerData.parentName,
        documents: playerData.documents
      };
      
      const response = await addPlayer(selectedTeamId, addData);
      if (response.success) {
        // Navigate back to player information page
        navigate('/my-club/player-information');
      }
    } catch (error: any) {
      console.error('Failed to save player:', error);
      setSubmitError(error.message || 'Failed to save player. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTeamChange = (newTeamId: string) => {
    setSelectedTeamId(newTeamId);
  };

  const handleCancel = () => {
    navigate('/my-club/player-information');
  };

  return (
    <div className="add-player-page">
      <Container fluid style={{ padding: '0 15px', marginTop: '20px' }}>
        {/* Breadcrumbs */}
        <div className="mb-3">
          <Breadcrumbs
            items={[
              { label: 'My Club' },
              { label: 'Player Information', onClick: () => navigate('/my-club/player-information') },
              { label: 'Add Player', active: true }
            ]}
          />
        </div>

        {/* Add Player Form */}
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Add New Player</h5>
              <Button 
                variant="link" 
                className="close-form-btn"
                onClick={handleCancel}
              >
                <i className="ri-close-line"></i>
              </Button>
            </div>
            
            {teamsError && (
              <div className="alert alert-danger mb-3" role="alert">
                {teamsError}
              </div>
            )}
            
            <PlayerForm
              player={null}
              teamId={selectedTeamId}
              teamName={teams.find(t => t.value === selectedTeamId)?.label || "Select Team"}
              ageCategories={[]} // Not needed anymore since age category comes from team
              teams={teams}
              showTeamField={true} // Show team field in Player Information
              onTeamChange={handleTeamChange}
              // No tournamentId prop - will use dynamic getCurrentActiveTournament()
              onSubmit={handleSavePlayer}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              submitError={submitError}
              teamAgeCategories={(() => {
                // Get selected team's age category
                const selectedTeam = fullTeamsData.find(team => team._id === selectedTeamId);
                if (selectedTeam && selectedTeam.ageCategory) {
                  return [selectedTeam.ageCategory];
                }
                return [];
              })()}
              hideAgeCategoryField={true} // Hide age category field since it's determined by team
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddPlayer; 